import {Record, List, Map} from 'immutable';
import moment from 'moment-timezone';
import numeral from 'numeral';

import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';
import {type DetailsData, type SortingConfig} from './types';

const defaultSortingConfig: SortingConfig = {
    column: 'Debt',
    direction: 'descending',
};
class OverviewData extends Record({
    categories: List<string>(),
    outstanding: List<number>(),
}) {}

export class DebtReportState extends Record<{
    institutionId: string | null;
    fetchingOverviewData: boolean;
    fetchingDetailData: boolean;
    overviewData: OverviewData;
    detailsData: Map<string, DetailsData>;
    billPayerIds: List<string>;
    sortingConfig: SortingConfig;
}>({
    institutionId: null,
    fetchingOverviewData: true,
    fetchingDetailData: true,
    overviewData: new OverviewData(),
    detailsData: Map<string, DetailsData>(),
    billPayerIds: List<string>(),
    sortingConfig: defaultSortingConfig,
}) {}

export default makeReducer<DebtReportState>(
    [
        match(Actions.showingInstitutionId, (state, {payload}) => {
            return state.merge({
                institutionId: payload.institutionId,
            });
        }),
        match(Actions.fetchOverviewData, state => {
            return state.merge({
                fetchingOverviewData: true,
            });
        }),
        match(Actions.fetchOverviewDataFailed, state => {
            return state.merge({
                fetchingOverviewData: false,
            });
        }),
        match(Actions.fetchOverviewDataSuccess, (state, action: any) => {
            return state.merge({
                fetchingOverviewData: false,
                overviewData: state.overviewData.merge({
                    categories: List(Object.keys(action.payload.debtOverview.balanceTimeline)),
                    outstanding: List(Object.values(action.payload.debtOverview.balanceTimeline)),
                }),
            });
        }),
        match(Actions.fetchData, state => {
            return state.merge({
                fetchingDetailData: true,
                detailsData: state.detailsData.keySeq().reduce((detailsData, billPayerId) => {
                    return detailsData.update(billPayerId, () => 'loading');
                }, state.detailsData),
            });
        }),
        match(Actions.fetchDataSuccess, (state, {payload}) => {
            return state.merge({
                billPayerIds: List(payload.billPayerIds),
                fetchingDetailData: false,
                detailsData: Map(payload.billPayerIds.map(billPayerId => [billPayerId, 'loading'])),
            });
        }),
        match(Actions.fetchBalanceData, (state, {payload}) => {
            const detailsData = payload.billPayerIds.reduce((detailsData, billPayerId) => {
                return detailsData.update(billPayerId, 'loading', () => 'loading');
            }, state.detailsData);

            return state.merge({
                detailsData,
            });
        }),
        match(Actions.fetchBalanceDataSuccess, (state, {payload}) => {
            const detailsData = Object.keys(payload.result).reduce((detailsData, billPayerId) => {
                return detailsData.update(billPayerId, 'loading', () => {
                    const result = payload.result[billPayerId];

                    const {lastPayment, lastPaymentReminderEmail} = result;

                    return {
                        balance: result.balance,
                        debt: result.debt,
                        overdue: result.overdue,
                        overdue30d: result.overdue30d,
                        lastPayment: lastPayment
                            ? {
                                  id: lastPayment.id,
                                  date: lastPayment.date,
                                  formattedDate: moment(lastPayment.date).format('LL'),
                                  formattedAmount: numeral(lastPayment.amount).format('$0,0.00'),
                                  paymentMethod: lastPayment.paymentMethod.title,
                                  note: lastPayment.note,
                              }
                            : null,
                        lastPaymentReminderEmail: lastPaymentReminderEmail
                            ? {
                                  id: lastPaymentReminderEmail.reminderId,
                                  createdAt: lastPaymentReminderEmail.createdAt,
                                  formattedDate: moment(lastPaymentReminderEmail.createdAt).format('LL'),
                              }
                            : null,
                    };
                });
            }, state.detailsData);

            const isStillFetching = detailsData
                .valueSeq()
                .toList()
                .some(item => item === 'loading');

            return state.merge({
                detailsData,
                fetchingDetailData: isStillFetching,
            });
        }),
        match(Actions.updateLatestPaymentReminders, (state, {payload}) => {
            const newDetailsData = payload.sentReminders.reduce((detailsData, reminder) => {
                return detailsData.update(reminder.billPayerId, detailsData => {
                    if (detailsData === 'loading') {
                        return detailsData;
                    }

                    return {
                        ...detailsData,
                        lastPaymentReminderEmail: {
                            createdAt: reminder.createdAt,
                            formattedDate: moment(reminder.createdAt).format('LL'),
                            id: reminder.paymentReminderEmailId,
                        },
                    };
                });
            }, state.detailsData);

            return state.merge({
                detailsData: newDetailsData,
            });
        }),
        match(Actions.updateSortBy, (state, {payload}) => {
            return state.merge({
                sortingConfig: nextSortingConfig(state.sortingConfig, payload),
            });
        }),
    ],
    new DebtReportState(),
);

// eslint-disable-next-line consistent-return
const nextSortingConfig = (currentState: SortingConfig, clickedColumn: SortingConfig['column']): SortingConfig => {
    if (currentState.column === clickedColumn) {
        // eslint-disable-next-line default-case
        switch (currentState.direction) {
            case 'ascending':
                return {
                    ...currentState,
                    direction: 'descending',
                };
            case 'descending':
                return defaultSortingConfig;
        }
    } else {
        return {
            column: clickedColumn,
            direction: 'ascending',
        };
    }
};
